import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Map as MapIcon } from "lucide-react";
import { CityMap } from "@/components/CityMap";
import { ShareButton } from "@/components/ShareButton";
import { MAP_CATEGORIES, MAP_PLACES } from "@/data/areas";
import { Seo, PageSchema } from "@/components/Seo";
import { GygTourBand } from "@/components/GetYourGuide";

// Day trips sit far outside the city, so they start hidden to keep the map zoomed in.
const DEFAULT_ON = { areas: true, sights: true, food: true, swim: true, daytrips: false };

export default function MapPage() {
  const [active, setActive] = useState(DEFAULT_ON);
  const toggle = (id) => setActive((a) => ({ ...a, [id]: !a[id] }));

  const markers = MAP_PLACES.filter((p) => active[p.category]);
  const catList = Object.values(MAP_CATEGORIES);

  return (
    <main data-testid="map-page" className="pt-16 md:pt-20">
      <Seo
        title="Copenhagen Map — Sights, Food, Bakeries & Neighbourhoods"
        description="An interactive map of Copenhagen's best sights, bakeries, harbour baths, restaurants and neighbourhoods — filter by category and plan your route."
        path="/map"
      />
      <PageSchema
        name="Copenhagen Map — Sights, Food, Bakeries & Neighbourhoods"
        description="An interactive map of Copenhagen's best sights, bakeries, harbour baths, restaurants and neighbourhoods — filter by category and plan your route."
        path="/map"
        crumb="Map"
      />
      <section className="relative overflow-hidden bg-[#13141A] text-white border-b border-zinc-200">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1584048139622-28fd9506af94?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxDb3BlbmhhZ2VuJTIwYWVyaWFsfGVufDB8fHx8MTc5MTQ2MTA0OHww&ixlib=rb-4.1.0&q=85" alt="" aria-hidden="true" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#13141A] via-[#13141A]/70 to-[#13141A]/40" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-16">
          <div className="flex items-center gap-3 mb-6">
            <Link
              to="/"
              data-testid="map-back-home"
              className="inline-flex items-center gap-2 font-body font-700 text-sm bg-black text-white px-5 py-2.5 rounded-full border border-white/20 hover:bg-white hover:text-black transition-colors"
            >
              <ArrowLeft size={16} /> Back to start
            </Link>
            <ShareButton title="The Whole City, Pinned — Copenhagen Map" testid="map-share" />
          </div>
          <div className="flex items-center gap-3 mb-3">
            <span className="font-body font-800 text-xs uppercase tracking-[0.3em] eyebrow text-white">THE WHOLE CITY, PINNED</span>
          </div>
          <h1 className="font-display font-900 tracking-tighter leading-[0.85] text-4xl sm:text-5xl lg:text-6xl">
            Copenhagen<br />
            <span className="font-editorial italic font-600">on one map</span>
          </h1>
          <p className="font-body text-base md:text-lg text-white/90 max-w-2xl mt-5">
            Every neighbourhood, sight, bakery and swim spot we mention — all on one map.
            Tap a pin to see what it is and open it straight in your own Maps app.
          </p>
        </div>
      </section>

      <section className="bg-[#F9F8F6]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-10 md:py-14">
          {/* colour-coded category toggles */}
          <div className="flex flex-wrap items-center gap-2.5 mb-6">
            {catList.map((c) => {
              const on = active[c.id];
              return (
                <button
                  key={c.id}
                  data-testid={`map-toggle-${c.id}`}
                  onClick={() => toggle(c.id)}
                  className="inline-flex items-center gap-2 font-body font-700 text-sm px-4 py-2.5 rounded-full border transition-all"
                  style={
                    on
                      ? { background: c.color, color: "#fff", borderColor: c.color }
                      : { background: "#fff", color: "#18181B", borderColor: "#E4E4E7" }
                  }
                >
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-full"
                    style={{ background: on ? "#fff" : c.color }}
                  />
                  {c.label}
                </button>
              );
            })}
            <span className="font-body text-sm text-black/50 ml-1">
              {markers.length} places · tap a pin for details
            </span>
          </div>

          <div className="rounded-2xl border border-zinc-200 shadow-brut-lg overflow-hidden h-[65vh] min-h-[440px]">
            <CityMap center={[55.686, 12.575]} zoom={12} scrollWheelZoom={true} markers={markers} />
          </div>
        </div>
      </section>

      <GygTourBand tourId="743612" label="STEP INSIDE THE PALACE" />
    </main>
  );
}
